import { Config } from "../../config";

import traverse from "traverse";

import { OnsipApiResponseBody, OnsipApiResponse } from "./apiResponse/response-body";
import { OnsipAPIError, OnsipAPIParameter } from "./apiResponse/context";
import { ApiFailure } from "./api-failure";

import {
  ApiActionType,
  ApiBrowseAction,
  ApiAddAction,
  ApiEditAction,
  ApiDeleteAction,
  ApiReadAction
} from "./api-actions";
//
// DEPRECATED - PRIVATE - DO NOT USE
// This is ONLY to be used by the ApiService.
// This is TypeScript a port of the onsip-api-action NPM module (which we would like to leave behind to die).
//

// TODO What's the best value for MAX_PIECE_WIDTH here? Empirically, from the tests, 1000 seems to be relatively fast...
const MAX_PIECE_WIDTH = 25000; // 25000 is the server-side max Limit, according to Nader

export interface IApiError {
  action: string;
  message: string;
  code: string;
  parameter: string | undefined;
  Response: OnsipApiResponse;
}
/**
 * Error thrown when the API request completed with one or more errors.
 */
export class ApiError extends Error implements IApiError {
  /**
   * Constructor
   * @param message Error message.
   * @param code The error code associated with the first api error.
   * @param parameter The request parameter associated with the first api error (if it exists).
   * @param Response The API "Response".
   */
  constructor(
    public action: string,
    public message: string,
    public code: string,
    public parameter: string | undefined,
    public Response: OnsipApiResponse
  ) {
    super(`${action} - ${message}`);
    Object.setPrototypeOf(this, new.target.prototype); // restore prototype chain
  }
}

/**
 * So, this feels counterintuitive to what the cleanObject function is doing.
 * Basically by removing all undefineds from an object, we make it so each property is
 * questionably still on the object which reads as undefined. So although nothing equals
 * undefined anymore, every property in an object now becomes
 * type Foo<T> = { [K in keyof T]: <T>[K] | undefined; } (which is basically just the Partial definition)
 */
type CleanObject<T> = { [K in keyof T]?: T[K] };

type BrowseParameters = CleanObject<{
  [K: string]: any;
  Offset?: number;
  Limit: number;
  CalcFound: boolean;
}>;

/**
 * Makes an API request (except "Browse" actions without a "Limit" query parameter).
 * If the API reqeust is successful (a 200 Ok is received from api server) and
 * the API request completed with no errors, returns a promise which resolves
 * with a JSON object embodying the API "Response". Otherwise, it rejects.
 *
 * FIXME: TODO: This function does not handle API Exception
 *  - http://api.onsip.com/webservices/schema/rng/Exception.rng
 *  - https://developer.onsip.com/docs/admin-api
 *
 *
 * @param actionName API action.
 * @param queryParameters API action parameters.
 * @reject {ApiError} The API request completed with one or more errors.
 * @reject {ApiFailure} The API server returned an non-200 response.
 * @reject {TypeError} Network error is encounted (not able to complete request with server).
 */
export function genericApiAction<T>(
  actionName: ApiActionType,
  queryParameters?: any
): Promise<OnsipApiResponse<string, T>> {
  queryParameters = cleanObject<any>(queryParameters || {});
  queryParameters.Action = actionName;
  queryParameters.Output = "json";
  queryParameters.AppUserAgent = "OnSIP_App/" + Config.VERSION_NUMBER + "/" + getPlatform(); // same as useragentstring in ua config

  if ((actionName as string).search(/Browse$/) >= 0 && !queryParameters.Limit) {
    const errorMessage =
      "Error running " +
      actionName +
      ":\n" +
      "Don't use action() for *Browse calls, use browse() instead.\n" +
      "If you *must* use action(), pass a Limit parameter.\n";

    return Promise.reject(new Error(errorMessage));
  }

  const query = querystringify(queryParameters);

  const xhrOptions = {
    method: "POST",
    body: query,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded"
    }
  };
  return fetch(Config.ADMIN_API_URL, xhrOptions)
    .then(wrapAPIException)
    .then(tryJSONparse)
    .then(fixEmptyStrings)
    .then(detectAPIErrors)
    .then(typeUnsafePluck("Response"));
}

/**
 * onsipApiBrowse aims to make an api browse using a genericApiActions,
 * then returns the lowest "level" of data in the response as an array
 * @param actionName API action.
 * @param queryParameters API action parameters.
 */
export function onsipApiBrowse<T>(
  actionName: ApiBrowseAction,
  queryParameters: Record<string, any> = {}
): Promise<Array<T>> {
  const cleanQueryParameters = cleanObject<any>(queryParameters);

  if (!("Limit" in cleanQueryParameters)) {
    // If there's no Limit parameter supplied, do an onsipApiCount and find it
    return onsipApiCount(actionName, cleanQueryParameters).then(Limit =>
      onsipApiBrowse(actionName, { ...cleanQueryParameters, Limit })
    );
  } else if (cleanQueryParameters.Limit > MAX_PIECE_WIDTH) {
    // if the supplied Limit parameter is too big, we need to do the browse in several calls
    // break the given params into an array of params describing several calls that span the entire length of the given Limit (which is over the max)
    const parametersList: Array<CleanObject<BrowseParameters>> = [];
    let offset = cleanQueryParameters.Offset || 0;
    let remaining = cleanQueryParameters.Limit;
    while (remaining > 0) {
      const limit = Math.min(MAX_PIECE_WIDTH, remaining);
      parametersList.push({
        ...cleanQueryParameters,
        Offset: offset,
        Limit: limit,
        CalcFound: false
      });
      offset += limit;
      remaining -= limit;
    }
    const browsePiecePromises = parametersList.map(browsePieceParameters =>
      onsipApiBrowse<T>(actionName, browsePieceParameters)
    );
    // execute the several api calls in parallel via Promise.all, then stitch the results together
    return Promise.all(browsePiecePromises).then(browseLists => {
      return ([] as Array<any>).concat.apply([], browseLists);
    });
  } else {
    // this is the actual browse
    return genericApiAction<Array<T>>(actionName, cleanQueryParameters).then(res => {
      const singular = singularFromBrowseActionName(actionName);
      const plural = pluralFromBrowseActionName(actionName);
      const maybeListOrItem = (res.Result[actionName] as any)[plural][singular];
      const arrayResponse = asArray(maybeListOrItem);
      (arrayResponse as any).raw = res; /* SKETCHY! But it seems to work
        (edit 6/18/20): a parallel-style browse breaks this, since it's a hack that we plan on removing later that only affects a few calls im leaving it */
      return arrayResponse;
    });
  }
}

/**
 * onsipApiAdd aims to make an api add call using a genericApiAction,
 * then returns the lowest "level" of data in the response unless the returned action object
 * - ie. the one that has [action]Add as the name - contains more than just the actionName object
 * or doesn't follow the Response: {[action]Add: { action: {some data} }}} model
 * @param actionName API action.
 * @param queryParameters API action parameters.
 */
export function onsipApiAdd<T>(actionName: ApiAddAction, queryParameters?: any): Promise<T> {
  const cleanActionName = (actionName as string).replace(/Add$/, "");
  let raw: OnsipApiResponse<string, T>;
  return (
    genericApiAction<T>(actionName, queryParameters)
      .then(res => {
        raw = res;
        return res;
      })
      .then(typeUnsafePluck("Result"))
      .then((res: any) => (res[actionName] ? typeUnsafePluck(actionName)(res) : res))
      // if the response under the action only includes the data in the action name return that data, otherwise if there's other info leave it all in
      // ex. TelephoneNumberAddressAdd => {TelephoneNumberAddress: TelephoneNumberAddress, Organization: Organization}
      .then((res: any) =>
        res[cleanActionName] && Object.keys(res).length === 1
          ? typeUnsafePluck(cleanActionName)(res)
          : res
      )
      .then(res => ({ ...res, raw }))
  );
}

/**
 * onsipApiEdit aims to make an api edit call using a genericApiAction,
 * then returns the lowest "level" of data in the response
 * @param actionName API action.
 * @param queryParameters API action parameters.
 */
export function onsipApiEdit<T>(actionName: ApiEditAction, queryParameters?: any): Promise<T> {
  const cleanActionName = (actionName as string).replace(/Edit$/, "");
  // some edits have different actionNames than their service, and the service can explicitly say
  // what data to pluck out of the response to keep the return types the same across API calls
  const resourceNameToPluck: string = queryParameters.resourceNameToPluck;
  delete queryParameters.resourceNameToPluck;
  let raw: OnsipApiResponse<string, T> = {} as OnsipApiResponse<string, T>;
  return genericApiAction<T>(actionName, queryParameters)
    .then(res => {
      raw = res;
      return res;
    })
    .then(typeUnsafePluck("Result"))
    .then((res: any) => (res[actionName] ? typeUnsafePluck(actionName)(res) : res))
    .then((res: any) => (res[cleanActionName] ? typeUnsafePluck(cleanActionName)(res) : res))
    .then((res: any) =>
      resourceNameToPluck && res[resourceNameToPluck]
        ? typeUnsafePluck(resourceNameToPluck)(res)
        : res
    )
    .then(res => ({ ...res, raw }));
}

/**
 * onsipApiDelete aims to make an api delete call using a genericApiAction,
 * then returns empty data since deletes return a Response: { Action: "" } json object
 * @param actionName API action.
 * @param queryParameters API action parameters.
 */
export function onsipApiDelete(actionName: ApiDeleteAction, queryParameters?: any): Promise<{}> {
  return genericApiAction(actionName, queryParameters).then(res => ({ raw: res }));
}

/**
 * onsipApiRead aims to make an api browse using a genericApiActions,
 * and returns the lowest "level" of data in the response
 * @param actionName API action.
 * @param queryParameters API action parameters.
 */
export function onsipApiRead<T>(actionName: ApiReadAction, queryParameters?: any): Promise<T> {
  const cleanActionName = (actionName as string).replace(/Read$/, "");
  let raw: OnsipApiResponse<string, T> = {} as OnsipApiResponse<string, T>;
  return genericApiAction<T>(actionName, queryParameters)
    .then(res => {
      raw = res;
      return res;
    })
    .then(typeUnsafePluck("Result"))
    .then((res: any) => (res[actionName] ? typeUnsafePluck(actionName)(res) : res))
    .then((res: any) => (res[cleanActionName] ? typeUnsafePluck(cleanActionName)(res) : res))
    .then(res => ({ ...res, raw }));
}

/*--------***** Internal browse helpers ******/

function singularFromBrowseActionName(actionName: ApiBrowseAction): string {
  switch (actionName) {
    // Add exceptions to the api json naming convention for Browse here.
    case ApiBrowseAction.ExtensionBrowse:
      return "UserAlias";
    default:
      return (actionName as string).replace(/SystemBrowse$|Browse$/, "");
  }
}

function pluralFromBrowseActionName(actionName: ApiBrowseAction): string {
  switch (actionName) {
    // Add exceptions to the api json naming convention for Browse here.
    case ApiBrowseAction.ExtensionBrowse:
      return "UserAliases";
    default:
      return (actionName as string).replace(/SystemBrowse$|Browse$/, "") + "s";
  }
}

/**
 * Does a browse action with the supplied actionName with an additional parameter that returns the number of entries in the database.
 * This is used as a helper to find the limit for browses where the limit is not originally supplied
 */
function onsipApiCount(
  actionName: ApiBrowseAction,
  queryParameters: CleanObject<any> = {}
): Promise<number> {
  const countParameters = {
    ...queryParameters, // this first so subsequent params are overridden if they exist in queryParameters
    Offset: 0,
    Limit: 1,
    CalcFound: true
  };
  return genericApiAction<any>(actionName, countParameters).then(response => {
    const expectedLength =
      response.Result[actionName][pluralFromBrowseActionName(actionName)]["@attributes"].Found;
    return Number(expectedLength);
  });
}

/*----------****** Internal helper functions ******/

/** The JSON API represents "" as {}, so fix it. */
// https://jnctn.lighthouseapp.com/projects/119540/tickets/568-jiffyphone-historical-events-csv-sometimes-shows-object-object
function fixEmptyStrings<T>(obj: T): T {
  traverse(obj).forEach(function (value: any) {
    if (isEmpty(value)) {
      // @ts-ignore: noImplicitThis
      this.update("");
    }
  });
  return obj;
}

function asArray<T>(maybeListOrItem: T | Array<T>): Array<T> {
  return ([] as Array<T>).concat(maybeListOrItem || []);
}

// http://developer.onsip.com/admin-api/#xml-response-format
function detectAPIErrors(body: OnsipApiResponseBody): OnsipApiResponseBody {
  const unknownApiError = (apiBody: OnsipApiResponseBody) => {
    const action = getApiActionName(apiBody);
    const name = action ? action.Value : "UNDEFINED";
    return new Error(`Action: ${name} has no errors`);
  };
  if (body.Response.Context.Request.IsValid !== "true") {
    // Not all api calls return an error even if IsValid !== true
    if (!body.Response.Context.Request.Errors) {
      throw unknownApiError(body);
    }
    throw wrapAPIError(body, body.Response.Context.Request.Errors.Error);
  }

  if (body.Response.Context.Action.IsCompleted !== "true") {
    if (!body.Response.Context.Action.Errors) {
      throw unknownApiError(body);
    }
    throw wrapAPIError(body, body.Response.Context.Action.Errors.Error);
  }
  return body;
}

// https://github.com/ianstormtaylor/is-empty
function isEmpty(val: any): boolean {
  const has = Object.prototype.hasOwnProperty;
  const toString = Object.prototype.toString;

  // Null and Undefined...
  // eslint-disable-next-line no-null/no-null
  if (val === null) {
    return true;
  }

  // Booleans...
  if ("boolean" === typeof val) {
    return false;
  }

  // Numbers...
  if ("number" === typeof val) {
    return val === 0;
  }

  // Strings...
  if ("string" === typeof val) {
    return val.length === 0;
  }

  // Functions...
  if ("function" === typeof val) {
    return val.length === 0;
  }

  // Arrays...
  if (Array.isArray(val)) {
    return val.length === 0;
  }

  // Errors...
  if (val instanceof Error) {
    return val.message === "";
  }

  // Objects...
  if (val.toString === toString) {
    switch (val.toString()) {
      // Maps, Sets, Files and Errors...
      case "[object File]":
      case "[object Map]":
      case "[object Set]": {
        return val.size === 0;
      }

      // Plain objects...
      case "[object Object]": {
        for (const key of Object.keys(val as object)) {
          if (has.call(val, key)) {
            return false;
          }
        }

        return true;
      }
    }
  }

  // Anything else...
  return false;
}

// https://github.com/stephenplusplus/propprop
function typeUnsafePluck<T extends {}>(propertyName: keyof T): (item: T) => T[typeof propertyName] {
  // @ts-ignore: NB: THIS IS AN EXTREMELY UNSAFE TYPE ASSERTION TO REMOVE undefined FROM THIS TYPE
  return (item: T): T[typeof propertyName] => {
    if (propertyName in item) {
      return item[propertyName];
    }
  };
}

function querystringify(obj: any): string {
  // eslint-disable-next-line no-null/no-null
  const mangledObj = Object.create(null);

  Object.keys(obj).forEach(key => {
    const value = obj[key];

    if (!Array.isArray(value)) {
      mangledObj[key] = value;
    } else {
      value.forEach((val, i) => {
        const mangledKey = key + "[" + i + "]";
        const mangledValue = val;
        mangledObj[mangledKey] = mangledValue;
      });
    }
  });

  return querystringEncode(mangledObj);
}

// https://github.com/Gozala/querystring
function stringifyPrimitive(v: string | boolean | number): string {
  switch (typeof v) {
    case "string":
      return v;

    case "boolean":
      return v ? "true" : "false";

    case "number":
      return isFinite(v) ? v.toString() : "";

    default:
      return "";
  }
}

function querystringEncode(obj: any, separator?: string, equals?: string, name?: string): string {
  separator = separator || "&";
  equals = equals || "=";
  // eslint-disable-next-line no-null/no-null
  if (obj === null) {
    obj = undefined;
  }

  if (typeof obj === "object") {
    return Object.keys(obj)
      .map(key => {
        const keyString = encodeURIComponent(stringifyPrimitive(key)) + equals;
        if (Array.isArray(obj[key])) {
          return obj[key]
            .map((v: string) => keyString + encodeURIComponent(stringifyPrimitive(v)))
            .join(separator);
        } else {
          return keyString + encodeURIComponent(stringifyPrimitive(obj[key]));
        }
      })
      .join(separator);
  } else if (!name) {
    return "";
  } else {
    return (
      encodeURIComponent(stringifyPrimitive(name)) +
      equals +
      encodeURIComponent(stringifyPrimitive(obj))
    );
  }
}

function tryJSONparse(response: Response): Promise<OnsipApiResponseBody> {
  return response.json();
}

function getApiActionName(body: OnsipApiResponseBody): OnsipAPIParameter | undefined {
  let parameters: Array<OnsipAPIParameter> = [];
  if (body.Response.Context.Request.Parameters) {
    parameters = asArray(body.Response.Context.Request.Parameters.Parameter);
  }
  const action = parameters.find(param => param.Name === "Action");
  // Parameter only exists on Request errors. Compare the following:
  // http://api.onsip.com/webservices/schema/rng/Request.rng
  // http://api.onsip.com/webservices/schema/rng/Action.rng
  return action;
}

function wrapAPIError(
  body: OnsipApiResponseBody,
  apiErrors: OnsipAPIError | Array<OnsipAPIError>
): ApiError {
  apiErrors = asArray(apiErrors);
  const apiError = apiErrors[0];
  const action = getApiActionName(body);
  return new ApiError(
    action ? action.Value : "ACTION NAME UNDEFINED",
    apiError.Message,
    apiError.Code,
    apiError.Parameter,
    body.Response
  );
}

async function wrapAPIException(response: Response): Promise<Response> {
  if (response.ok) {
    return response;
  } else {
    return response.text().then((text: string) => {
      throw new ApiFailure(text);
    });
  }
}

// previously, we used JSON.parse(JSON.stringify(x)) to clean up objects. This got rid of all
// undefineds and empty functions. Here we more deliberately mimic that behavior
export function cleanObject<T>(val: any): CleanObject<T> {
  Object.keys(val).forEach(key => {
    const value = val[key];
    if (value === undefined || ("function" === typeof value && value.length === 0)) delete val[key];
    else if (!isEmpty(value) && typeof value === "object") cleanObject<typeof value>(val[key]);
  });
  return val;
}

function getPlatform(): string {
  if (Config.IS_WEB) {
    return "web";
  }

  if (Config.IS_DESKTOP && Config.IS_WIN) {
    return "windows";
  }

  if (Config.IS_DESKTOP && Config.IS_MAC) {
    return "mac";
  }

  if (Config.IS_DESKTOP && Config.IS_LINUX) {
    return "linux";
  }

  if (Config.IS_ANDROID) {
    return "android";
  }

  if (Config.IS_IOS) {
    return "ios";
  }

  return "impossible";
}
